<script>
import axios from "axios";
import moment from "moment";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import { countries } from "@/utils/countrys.js";
import { ALLOGGIATI_STATI } from "@/utils/alloggiatiStati.js";

export default {
  name: "CheckinEditDrawer",
  components: { VueTelInput },
  // Portal a <body>: este drawer se abre dentro del detalle de reserva (Sidebar
  // PrimeVue con z-index alto). Sin portarlo, su overlay quedaba por debajo/atrapado
  // por el contexto de apilamiento del Sidebar y no se veía.
  directives: {
    portalBody: {
      inserted(el) { document.body.appendChild(el); },
      unbind(el) { if (el && el.parentNode) el.parentNode.removeChild(el); },
    },
  },
  props: {
    visible:       { type: Boolean, default: false },
    historial:     { type: Object,  default: null  },
    property:      { type: Object,  required: true },
    isVisibleData: { type: Boolean, default: false },
  },
  data() {
    return {
      saving: false,
      errorMsg: "",
      defaultCountryCode: "ES",

      // Búsqueda para selects de Italia
      _paeseSearch:   "",
      _rilascioSearch: "",

      form: {
        nombre:    "",
        apellido:  "",
        email:     "",
        phone:     "",

        date_of_birth: null,
        sex: "",

        fecha_ingreso: null,
        fecha_salida:  null,

        type_documento: "",
        nu_documento:   "",

        // España: número de soporte
        document_support: "",

        // ISO2 (España/Colombia/otros) o ISO3 (Italia)
        pais_code: "",

        // metadata genérica
        pais_iso3:    "",
        pais_name_es: "",
        pais_name_en: "",

        // Italia
        paese_nascita_code: "",   // 9 dígitos Alloggiati (Stato Nascita / Cittadinanza)
        luogo_rilascio_doc: "",   // ISO3 del país de expedición del documento
      },
    };
  },

  computed: {
    // ─── País de la propiedad ──────────────────────────────────────────────
    propertyCountry() {
      const code = String(this.property?.code_pais || "").toUpperCase();
      if (code === "ESP") return "ES";
      if (code === "ITA") return "IT";
      return code;
    },
    isITProperty() {
      return this.propertyCountry === "IT";
    },
    isCOProperty() {
      return this.propertyCountry === "CO";
    },

    // ─── Nacionalidad del huésped (ISO2) ──────────────────────────────────
    guestNationalityISO2() {
      if (this.isITProperty) {
        // Para Italia, pais_code guarda ISO3 → normalizar a ISO2
        const raw = String(this.form.pais_code || "").trim().toUpperCase();
        if (/^[A-Z]{3}$/.test(raw)) {
          const hit = this.nationalityOptions.find((c) => c.iso3 === raw);
          return hit?.iso2 || "";
        }
        return /^[A-Z]{2}$/.test(raw) ? raw : "";
      }
      return this.normalizeCountryToISO2(this.form.pais_code);
    },
    isESGuest() {
      return this.guestNationalityISO2 === "ES";
    },

    // ─── Edad ─────────────────────────────────────────────────────────────
    age() {
      const dob = this.form.date_of_birth;
      if (!dob) return null;
      const m = moment(dob, "YYYY-MM-DD", true);
      if (!m.isValid()) return null;
      const years = moment().diff(m, "years");
      return Number.isFinite(years) ? years : null;
    },
    isMinor() {
      return this.age !== null ? this.age < 18 : false;
    },
    isChildUnder7CO() {
      if (!this.isCOProperty) return false;
      return this.age !== null ? this.age < 7 : false;
    },

    // ─── Reglas de documento ──────────────────────────────────────────────
    requiresDocFields() {
      if (this.isITProperty)          return !this.isMinor;
      if (this.propertyCountry === "ES") return !this.isMinor;
      if (this.isCOProperty)          return !this.isChildUnder7CO;
      return true;
    },
    showSupportField() {
      return !this.isITProperty && this.isESGuest;
    },
    requiresSupportNumber() {
      return !this.isITProperty && this.isESGuest && !this.isMinor;
    },

    // ─── Opciones de tipo de documento ───────────────────────────────────
    docTypeOptions() {
      if (this.isITProperty) {
        return [
          { value: "IDENT",  label: this.$t("checkinEditDrawer.docTypes.it.IDENT")  },
          { value: "IDELE",  label: this.$t("checkinEditDrawer.docTypes.it.IDELE") },
          { value: "PASOR",  label: this.$t("checkinEditDrawer.docTypes.it.PASOR")     },
          { value: "PASSE",  label: this.$t("checkinEditDrawer.docTypes.it.PASSE")   },
          { value: "PASDI",  label: this.$t("checkinEditDrawer.docTypes.it.PASDI")   },
          { value: "PATEN",  label: this.$t("checkinEditDrawer.docTypes.it.PATEN")     },
          { value: "PATNA",  label: this.$t("checkinEditDrawer.docTypes.it.PATNA")         },
          { value: "CIDIP",  label: this.$t("checkinEditDrawer.docTypes.it.CIDIP")          },
          { value: "RIFUG",  label: this.$t("checkinEditDrawer.docTypes.it.RIFUG")  },
        ];
      }
      if (this.isCOProperty) {
        return [
          { value: "CC",  label: this.$t("checkinEditDrawer.docTypes.co.CC")              },
          { value: "CE",  label: this.$t("checkinEditDrawer.docTypes.co.CE")             },
          { value: "P",   label: this.$t("checkinEditDrawer.docTypes.co.P")                         },
          { value: "PPT", label: this.$t("checkinEditDrawer.docTypes.co.PPT")   },
          { value: "PEP", label: this.$t("checkinEditDrawer.docTypes.co.PEP")   },
          { value: "DE",  label: this.$t("checkinEditDrawer.docTypes.co.DE")              },
          { value: "CD",  label: this.$t("checkinEditDrawer.docTypes.co.CD")                 },
        ];
      }
      if (this.propertyCountry === "ES") {
        return [
          { value: "ID",   label: this.$t("checkinEditDrawer.docTypes.es.ID")       },
          { value: "IM",   label: this.$t("checkinEditDrawer.docTypes.es.IM")       },
          { value: "P",    label: this.$t("checkinEditDrawer.docTypes.es.P") },
          { value: "OTRO", label: this.$t("checkinEditDrawer.docTypes.es.OTRO")      },
        ];
      }
      return [
        { value: "ID",   label: this.$t("checkinEditDrawer.docTypes.generic.ID") },
        { value: "P",    label: this.$t("checkinEditDrawer.docTypes.generic.P")              },
        { value: "OTRO", label: this.$t("checkinEditDrawer.docTypes.generic.OTRO")                   },
      ];
    },

    // ─── Opciones de nacionalidad (ISO2, para no-Italia) ──────────────────
    nationalityOptions() {
      const list = Array.isArray(countries) ? countries : [];
      const map = new Map();
      const score = (x) => {
        let s = 0;
        if (x.iso3) s += 1;
        if (x.es)   s += 2;
        if (x.iso2 === "GB" && String(x.es || "").toLowerCase().includes("reino unido")) s += 10;
        return s;
      };
      for (const c of list) {
        if (!c?.iso2) continue;
        const iso2 = String(c.iso2).trim().toUpperCase();
        if (!iso2) continue;
        const normalized = {
          iso2,
          iso3:      String(c.iso3 || "").trim().toUpperCase(),
          es:        c.es || "",
          en:        c.en || "",
          phoneCode: c.phoneCode || "",
        };
        if (!map.has(iso2)) { map.set(iso2, normalized); continue; }
        if (score(normalized) > score(map.get(iso2))) map.set(iso2, normalized);
      }
      return Array.from(map.values()).sort((a, b) =>
        a.es.localeCompare(b.es, "es", { sensitivity: "base" })
      );
    },

    // ─── Opciones Alloggiati filtradas ────────────────────────────────────
    alloggiatiPaeseOptions() {
      const q = (this._paeseSearch || "").trim().toLowerCase();
      if (!q) return ALLOGGIATI_STATI;
      return ALLOGGIATI_STATI.filter(
        (s) =>
          s.es.toLowerCase().includes(q) ||
          s.en.toLowerCase().includes(q) ||
          s.nome.toLowerCase().includes(q) ||
          s.iso3.toLowerCase().includes(q)
      );
    },
    alloggiatiRilascioOptions() {
      const q = (this._rilascioSearch || "").trim().toLowerCase();
      if (!q) return ALLOGGIATI_STATI;
      return ALLOGGIATI_STATI.filter(
        (s) =>
          s.es.toLowerCase().includes(q) ||
          s.en.toLowerCase().includes(q) ||
          s.nome.toLowerCase().includes(q) ||
          s.iso3.toLowerCase().includes(q)
      );
    },

    // ─── País nacimiento Italia seleccionado ──────────────────────────────
    selectedPaeseEntry() {
      return ALLOGGIATI_STATI.find((s) => s.code === this.form.paese_nascita_code) || null;
    },
    selectedRilascioEntry() {
      return ALLOGGIATI_STATI.find((s) => s.iso3 === this.form.luogo_rilascio_doc) || null;
    },

    // ─── ¿Puede guardar? ─────────────────────────────────────────────────
    canSave() {
      const emailOk = this.isValidEmail(this.form.email);
      const basicOk =
        this.form.nombre.trim() &&
        this.form.apellido.trim() &&
        emailOk &&
        this.form.phone &&
        this.form.date_of_birth &&
        this.form.sex &&
        this.form.fecha_ingreso &&
        this.form.fecha_salida;
      if (!basicOk) return false;
      if (this.form.fecha_salida < this.form.fecha_ingreso) return false;

      // Italia: requiere país nacimiento siempre
      if (this.isITProperty) {
        if (!this.form.paese_nascita_code) return false;
        if (this.requiresDocFields) {
          if (!this.form.type_documento)      return false;
          if (!this.form.nu_documento.trim())  return false;
          if (!this.form.luogo_rilascio_doc)   return false;
        }
        return true;
      }

      // Genérico
      if (!this.guestNationalityISO2) return false;
      if (this.requiresDocFields) {
        if (!this.form.type_documento)     return false;
        if (!this.form.nu_documento.trim()) return false;
      }
      if (this.requiresSupportNumber && !this.form.document_support.trim()) return false;
      return true;
    },
  },

  watch: {
    visible(val) {
      if (val) {
        this.errorMsg = "";
        this._paeseSearch   = "";
        this._rilascioSearch = "";
        this._updatePhoneCountry();
        this._loadFromHistorial();
        this.$nextTick(() => this.$refs.firstField?.focus?.());
      }
    },
    property: { deep: true, handler() { this._updatePhoneCountry(); } },
    "form.fecha_ingreso"(v) {
      if (this.form.fecha_salida && v && this.form.fecha_salida < v) {
        this.form.fecha_salida = v;
      }
    },
    // Para no-Italia: normalizar ISO2
    "form.pais_code"(v) {
      if (this.isITProperty) return; // Italy: pais_code = ISO3, no normalizar
      const iso2 = this.normalizeCountryToISO2(v);
      if (iso2 && iso2 !== v) this.form.pais_code = iso2;
      this.setNationalityMetaByISO2(iso2);
      if (iso2 !== "ES") this.form.document_support = "";
    },
    // Para Italia: cuando cambia paese_nascita_code → actualizar pais_code (ISO3)
    "form.paese_nascita_code"(code) {
      if (!this.isITProperty) return;
      const entry = ALLOGGIATI_STATI.find((s) => s.code === code);
      this.form.pais_code    = entry?.iso3 || "";
      this.form.pais_iso3    = entry?.iso3 || "";
      this.form.pais_name_es = entry?.es   || "";
      this.form.pais_name_en = entry?.en   || "";
    },
  },

  methods: {
    toDateInput(value) {
      if (!value) return null;
      if (typeof value === "string") {
        const s = value.trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
        if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
          const [d, m, y] = s.split("-");
          return `${y}-${m}-${d}`;
        }
      }
      const d = new Date(value);
      if (isNaN(d.getTime())) return null;
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    },
    normalizePhone(val) {
      if (!val) return "";
      return String(val).replace(/\s+/g, "").replace(/^\++/, "+");
    },
    normalizeDoc(val) {
      return val ? String(val).trim().toUpperCase() : "";
    },
    isValidEmail(e) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());
    },
    _setError(msg) { this.errorMsg = msg || ""; },

    normalizeCountryToISO2(code) {
      if (!code) return "";
      const raw = String(code).trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(raw)) return raw;
      if (/^[A-Z]{3}$/.test(raw)) {
        const hit = (this.nationalityOptions || []).find((c) => c.iso3 === raw);
        return hit ? hit.iso2 : "";
      }
      return "";
    },

    setNationalityMetaByISO2(iso2) {
      const code = String(iso2 || "").trim().toUpperCase();
      if (!code) { this.form.pais_iso3 = this.form.pais_name_es = this.form.pais_name_en = ""; return; }
      const hit = (this.nationalityOptions || []).find((c) => c.iso2 === code);
      this.form.pais_iso3    = hit?.iso3 || "";
      this.form.pais_name_es = hit?.es   || "";
      this.form.pais_name_en = hit?.en   || "";
    },

    _updatePhoneCountry() {
      let code = String(this.property?.code_pais || "ES").toUpperCase();
      if (code === "ESP") code = "ES";
      if (code === "ITA") code = "IT";
      if (code.length > 2) code = code.slice(0, 2);
      this.defaultCountryCode = code || "ES";
    },

    _loadFromHistorial() {
      const h = this.historial || {};
      const c = h.cliente || {};

      this.form.nombre   = h.nombre   || "";
      this.form.apellido = h.apellido || "";
      this.form.email    = c.email    || h.email    || "";
      this.form.phone    = this.normalizePhone(c.whatsapp || h.telefono || "");

      this.form.date_of_birth = this.toDateInput(c.date_of_birth);
      this.form.sex           = c.sex || h.sex || "";

      this.form.fecha_ingreso = this.toDateInput(c.fecha_desde_hospedaje || h.fecha_ingreso);
      this.form.fecha_salida  = this.toDateInput(c.fecha_hospedaje       || h.fecha_salida);

      this.form.type_documento = h.type_documento || c.type_documento || "";
      this.form.nu_documento   = this.normalizeDoc(h.nu_documento || c.nu_documento || "");
      this.form.document_support = this.normalizeDoc(h.document_support || c.document_support || "");

      if (this.isITProperty) {
        // Cargar paese_nascita_code
        this.form.paese_nascita_code = c.paese_nascita_code || "";
        // Si no hay code, intentar derivar desde pais_code (ISO3)
        if (!this.form.paese_nascita_code && c.pais_code) {
          const iso3 = String(c.pais_code).trim().toUpperCase();
          const entry = ALLOGGIATI_STATI.find((s) => s.iso3 === iso3);
          this.form.paese_nascita_code = entry?.code || "";
        }
        // pais_code (ISO3)
        const entryByCode = ALLOGGIATI_STATI.find((s) => s.code === this.form.paese_nascita_code);
        this.form.pais_code    = entryByCode?.iso3    || c.pais_code    || "";
        this.form.pais_iso3    = entryByCode?.iso3    || "";
        this.form.pais_name_es = entryByCode?.es      || "";
        this.form.pais_name_en = entryByCode?.en      || "";

        // Lugar de expedición (ISO3)
        const rilascioRaw = c.luogo_rilascio_doc || "";
        // Puede ser ISO3 o código 9 dígitos
        if (/^\d{9}$/.test(rilascioRaw)) {
          const rEntry = ALLOGGIATI_STATI.find((s) => s.code === rilascioRaw);
          this.form.luogo_rilascio_doc = rEntry?.iso3 || "";
        } else {
          this.form.luogo_rilascio_doc = rilascioRaw;
        }
        // Para no-Italia: no mostramos soporte
        this.form.document_support = "";
      } else {
        this.form.paese_nascita_code = "";
        this.form.luogo_rilascio_doc = "";

        const rawPais = c.pais_code || h.pais_code || c.code || "";
        const iso2 = this.normalizeCountryToISO2(rawPais);
        this.form.pais_code    = iso2;
        this.form.pais_iso3    = String(c.code || "").trim().toUpperCase();
        this.form.pais_name_es = String(c.nacionalidad || "").trim();
        if (!this.form.pais_iso3 || !this.form.pais_name_es) {
          this.setNationalityMetaByISO2(iso2);
          if (String(c.nacionalidad || "").trim()) this.form.pais_name_es = String(c.nacionalidad || "").trim();
          if (String(c.code || "").trim())         this.form.pais_iso3    = String(c.code || "").trim().toUpperCase();
        }
        if (iso2 !== "ES") this.form.document_support = "";
      }
    },

    close() { this.$emit("close"); },

    _validateBeforeSave() {
      if (!this.form.nombre.trim() || !this.form.apellido.trim()) return this.$t("checkinEditDrawer.errors.nameRequired");
      if (!this.isValidEmail(this.form.email))                    return this.$t("checkinEditDrawer.errors.emailInvalid");
      if (!this.form.phone)                                       return this.$t("checkinEditDrawer.errors.phoneRequired");
      if (!this.form.date_of_birth)                              return this.$t("checkinEditDrawer.errors.birthDateRequired");
      if (!this.form.sex)                                        return this.$t("checkinEditDrawer.errors.sexRequired");
      if (!this.form.fecha_ingreso || !this.form.fecha_salida)   return this.$t("checkinEditDrawer.errors.stayDatesRequired");
      if (this.form.fecha_salida < this.form.fecha_ingreso)      return this.$t("checkinEditDrawer.errors.checkoutBeforeCheckin");

      if (this.isITProperty) {
        if (!this.form.paese_nascita_code) return this.$t("checkinEditDrawer.errors.birthCountryRequired");
        if (this.requiresDocFields) {
          if (!this.form.type_documento)     return this.$t("checkinEditDrawer.errors.docTypeRequired");
          if (!this.form.nu_documento.trim()) return this.$t("checkinEditDrawer.errors.docNumberRequired");
          if (!this.form.luogo_rilascio_doc)  return this.$t("checkinEditDrawer.errors.issuePlaceRequired");
        }
        return "";
      }

      if (!this.guestNationalityISO2) return this.$t("checkinEditDrawer.errors.nationalityRequired");
      if (this.requiresDocFields) {
        if (!this.form.type_documento)     return this.$t("checkinEditDrawer.errors.docTypeRequired");
        if (!this.form.nu_documento.trim()) return this.$t("checkinEditDrawer.errors.docNumberRequired");
      }
      if (this.requiresSupportNumber && !this.form.document_support.trim()) {
        return this.$t("checkinEditDrawer.errors.supportNumberRequired");
      }
      return "";
    },

    async save() {
      this._setError("");
      const msg = this._validateBeforeSave();
      if (msg) { this._setError(msg); return; }

      try {
        this.saving = true;

        let cliente_data, historial_data;

        if (this.isITProperty) {
          // Obtener entry Alloggiati para paese_nascita
          const paeseEntry = ALLOGGIATI_STATI.find((s) => s.code === this.form.paese_nascita_code);
          // Obtener entry Alloggiati para luogo rilascio
          const rilascioEntry = this.form.luogo_rilascio_doc
            ? ALLOGGIATI_STATI.find((s) => s.iso3 === this.form.luogo_rilascio_doc)
            : null;

          cliente_data = {
            whatsapp:  this.normalizePhone(this.form.phone),
            email:     String(this.form.email || "").trim(),
            date_of_birth: this.form.date_of_birth,
            sex:           this.form.sex,
            fecha_desde_hospedaje: this.form.fecha_ingreso,
            fecha_hospedaje:       this.form.fecha_salida,
            type_documento: this.requiresDocFields ? (this.form.type_documento || "") : "",
            nu_documento:   this.requiresDocFields ? this.normalizeDoc(this.form.nu_documento) : "",
            document_support: "",
            // Italia: pais_code = ISO3 (ciudadanía/nacimiento)
            pais_code:          paeseEntry?.iso3 || this.form.pais_code || "",
            pais_iso3:          paeseEntry?.iso3 || "",
            pais_name_es:       paeseEntry?.es   || "",
            pais_name_en:       paeseEntry?.en   || "",
            // Alloggiati-específicos
            paese_nascita_code: this.form.paese_nascita_code,
            luogo_rilascio_doc: this.requiresDocFields ? (rilascioEntry?.iso3 || this.form.luogo_rilascio_doc || "") : "",
          };

          historial_data = {
            nombre:          String(this.form.nombre   || "").trim(),
            apellido:        String(this.form.apellido || "").trim(),
            type_documento:  this.requiresDocFields ? (this.form.type_documento || "") : "",
            nu_documento:    this.requiresDocFields ? this.normalizeDoc(this.form.nu_documento) : "",
            pais_code:       paeseEntry?.iso3 || this.form.pais_code || "",
            document_support: "",
          };
        } else {
          const paisISO2 = this.guestNationalityISO2;
          this.setNationalityMetaByISO2(paisISO2);

          cliente_data = {
            whatsapp:  this.normalizePhone(this.form.phone),
            email:     String(this.form.email || "").trim(),
            date_of_birth: this.form.date_of_birth,
            sex:           this.form.sex,
            fecha_desde_hospedaje: this.form.fecha_ingreso,
            fecha_hospedaje:       this.form.fecha_salida,
            type_documento:  this.form.type_documento || "",
            nu_documento:    this.normalizeDoc(this.form.nu_documento),
            document_support: this.isESGuest ? this.normalizeDoc(this.form.document_support) : "",
            pais_code:    paisISO2,
            pais_iso3:    this.form.pais_iso3    || "",
            pais_name_es: this.form.pais_name_es || "",
            pais_name_en: this.form.pais_name_en || "",
            paese_nascita_code: "",
            luogo_rilascio_doc: "",
          };

          historial_data = {
            nombre:          String(this.form.nombre   || "").trim(),
            apellido:        String(this.form.apellido || "").trim(),
            type_documento:  this.form.type_documento || "",
            nu_documento:    this.normalizeDoc(this.form.nu_documento),
            pais_code:       paisISO2,
            document_support: this.isESGuest ? this.normalizeDoc(this.form.document_support) : "",
          };
        }

        const { data } = await axios.post("/checkin/update", {
          historial_id: this.historial?.id,
          cliente_id:   this.historial?.cliente_id,
          historial_data,
          cliente_data,
        });

        if (!data?.ok) throw new Error(data?.message || this.$t("checkinEditDrawer.errors.saveFailed"));

        this.$toast?.success?.(this.$t("checkinEditDrawer.toast.saved"));
        this.$emit("saved", data.historial);
        this.close();
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || this.$t("checkinEditDrawer.errors.saveError");
        this._setError(msg);
        this.$toast?.error?.(msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
    <!-- Portal a <body> (v-portal-body): escapa el contexto de apilamiento del
         Sidebar del detalle de reserva. Sin transición para no atascar el fade. -->
    <div v-if="visible" v-portal-body class="drawer-overlay" @mousedown.self="close">
      <div class="drawer-container" @mousedown.stop @click.stop>

        <!-- ─── HEADER ──────────────────────────────────────────────────── -->
        <div class="drawer-header">
          <div>
            <div class="drawer-title">{{ $t("checkinEditDrawer.header.title") }}</div>
            <div class="drawer-subtitle" v-if="historial">
              {{ historial.nombre }} {{ historial.apellido }}
            </div>
            <div class="drawer-meta" v-if="age !== null">
              {{ $t("checkinEditDrawer.header.age") }} <strong>{{ age }}</strong>
              <span v-if="isITProperty && isMinor">
                &nbsp;· {{ $t("checkinEditDrawer.header.docNotRequiredMinor") }}
              </span>
              <span v-else-if="propertyCountry === 'ES' && isMinor">
                &nbsp;· {{ $t("checkinEditDrawer.header.docOptionalMinorES") }}
              </span>
              <span v-else-if="isCOProperty && isChildUnder7CO">
                &nbsp;· {{ $t("checkinEditDrawer.header.docOptionalMinorCO") }}
              </span>
            </div>
          </div>
          <button class="drawer-close" @click="close" :disabled="saving" type="button">
            <i class="fa-regular fa-times"></i>
          </button>
        </div>

        <!-- ─── BODY ────────────────────────────────────────────────────── -->
        <div class="drawer-body">
          <div v-if="errorMsg" class="drawer-alert">
            <i class="fa-solid fa-circle-exclamation"></i>
            {{ errorMsg }}
          </div>

          <!-- DATOS BÁSICOS -->
          <div class="drawer-section">
            <div class="section-header">
              <i class="fa-solid fa-user"></i>
              <span>{{ $t("checkinEditDrawer.sections.basicData") }}</span>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.firstName") }} *</label>
                <input ref="firstField" type="text" class="form-input" v-model="form.nombre" :disabled="saving" />
              </div>
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.lastName") }} *</label>
                <input type="text" class="form-input" v-model="form.apellido" :disabled="saving" />
              </div>
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.email") }} *</label>
                <input type="email" class="form-input" v-model="form.email" :disabled="saving" />
              </div>
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.phone") }} *</label>
                <VueTelInput
                  v-model="form.phone"
                  :key="defaultCountryCode"
                  :autoDefaultCountry="true"
                  :defaultCountry="defaultCountryCode"
                  :inputOptions="{ showDialCode: true }"
                  class="form-control"
                />
              </div>
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.birthDate") }} *</label>
                <input type="date" class="form-input" v-model="form.date_of_birth" :disabled="saving" />
              </div>
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.sex") }} *</label>
                <select class="form-input" v-model="form.sex" :disabled="saving">
                  <option value="" disabled>{{ $t("checkinEditDrawer.placeholders.select") }}</option>
                  <option value="M">{{ $t("checkinEditDrawer.sex.male") }}</option>
                  <option value="F">{{ $t("checkinEditDrawer.sex.female") }}</option>
                  <option value="Prefiero no decirlo">{{ $t("checkinEditDrawer.sex.preferNotToSay") }}</option>
                  <option value="Otro">{{ $t("checkinEditDrawer.sex.other") }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- FECHAS HOSPEDAJE -->
          <div class="drawer-section">
            <div class="section-header">
              <i class="fa-solid fa-calendar-days"></i>
              <span>{{ $t("checkinEditDrawer.sections.stayDates") }}</span>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.checkIn") }} *</label>
                <input type="date" class="form-input" v-model="form.fecha_ingreso" :disabled="saving" />
              </div>
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.checkOut") }} *</label>
                <input type="date" class="form-input" v-model="form.fecha_salida" :min="form.fecha_ingreso || undefined" :disabled="saving" />
              </div>
            </div>
          </div>

          <!-- ═══════════════════════════════════════════════════════════════
               DOCUMENTO — ITALIA
          ════════════════════════════════════════════════════════════════ -->
          <div class="drawer-section drawer-section--italy" v-if="isITProperty">
            <div class="section-header section-header--italy">
              <span class="section-flag">🇮🇹</span>
              <span>{{ $t("checkinEditDrawer.sections.italyDoc") }}</span>
              <span class="section-badge">{{ $t("checkinEditDrawer.italy.badge") }}</span>
            </div>

            <!-- País de nacimiento / Cittadinanza -->
            <div class="form-group form-group--full">
              <label>{{ $t("checkinEditDrawer.italy.birthCountryLabel") }} *
                <span class="hint-inline">{{ $t("checkinEditDrawer.italy.birthCountryHint") }}</span>
              </label>
              <!-- Búsqueda -->
              <input
                type="text"
                class="form-input form-input--search"
                v-model="_paeseSearch"
                :placeholder="$t('checkinEditDrawer.placeholders.searchCountry')"
                :disabled="saving"
              />
              <select
                class="form-input"
                v-model="form.paese_nascita_code"
                :disabled="saving"
                size="1"
              >
                <option value="" disabled>{{ $t("checkinEditDrawer.placeholders.selectCountry") }}</option>
                <option
                  v-for="s in alloggiatiPaeseOptions"
                  :key="s.code"
                  :value="s.code"
                >
                  {{ s.es }} ({{ s.iso3 }})
                </option>
              </select>
              <div class="field-preview" v-if="selectedPaeseEntry">
                <span class="fp-label">{{ $t("checkinEditDrawer.italy.alloggiatiCode") }}</span>
                <code class="fp-code">{{ selectedPaeseEntry.code }}</code>
                <span class="fp-label">{{ $t("checkinEditDrawer.italy.iso3Label") }}</span>
                <code class="fp-code">{{ selectedPaeseEntry.iso3 }}</code>
              </div>
            </div>

            <!-- Documento (solo adultos) -->
            <template v-if="requiresDocFields">
              <div class="form-grid" style="margin-top:12px">
                <div class="form-group">
                  <label>{{ $t("checkinEditDrawer.fields.docType") }} *</label>
                  <select class="form-input" v-model="form.type_documento" :disabled="saving">
                    <option value="" disabled>{{ $t("checkinEditDrawer.placeholders.select") }}</option>
                    <option v-for="o in docTypeOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>{{ $t("checkinEditDrawer.fields.docNumber") }} *</label>
                  <input type="text" class="form-input" v-model="form.nu_documento" :disabled="saving" :placeholder="$t('checkinEditDrawer.placeholders.docNumberExample')" />
                </div>
              </div>

              <!-- Lugar de expedición -->
              <div class="form-group form-group--full" style="margin-top:12px">
                <label>{{ $t("checkinEditDrawer.italy.issuePlaceLabel") }} *
                  <span class="hint-inline">{{ $t("checkinEditDrawer.italy.issuePlaceHint") }}</span>
                </label>
                <input
                  type="text"
                  class="form-input form-input--search"
                  v-model="_rilascioSearch"
                  :placeholder="$t('checkinEditDrawer.placeholders.searchCountry')"
                  :disabled="saving"
                />
                <select
                  class="form-input"
                  v-model="form.luogo_rilascio_doc"
                  :disabled="saving"
                >
                  <option value="" disabled>{{ $t("checkinEditDrawer.placeholders.selectCountry") }}</option>
                  <option
                    v-for="s in alloggiatiRilascioOptions"
                    :key="s.iso3"
                    :value="s.iso3"
                  >
                    {{ s.es }} ({{ s.iso3 }})
                  </option>
                </select>
                <div class="field-preview" v-if="selectedRilascioEntry">
                  <span class="fp-label">{{ $t("checkinEditDrawer.italy.alloggiatiCode") }}</span>
                  <code class="fp-code">{{ selectedRilascioEntry.code }}</code>
                </div>
              </div>
            </template>

            <div class="italy-minor-note" v-else>
              <i class="fa-solid fa-circle-info"></i>
              {{ $t("checkinEditDrawer.italy.minorNote") }}
            </div>
          </div>

          <!-- ═══════════════════════════════════════════════════════════════
               DOCUMENTO — ESPAÑA / COLOMBIA / GENÉRICO
          ════════════════════════════════════════════════════════════════ -->
          <div class="drawer-section" v-else>
            <div class="section-header">
              <i class="fa-solid fa-id-card"></i>
              <span>{{ $t("checkinEditDrawer.sections.doc") }}</span>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.docType") }} <span v-if="requiresDocFields">*</span></label>
                <select class="form-input" v-model="form.type_documento" :disabled="saving">
                  <option value="" :disabled="requiresDocFields">{{ $t("checkinEditDrawer.placeholders.select") }}</option>
                  <option v-for="o in docTypeOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>{{ $t("checkinEditDrawer.fields.docNumber") }} <span v-if="requiresDocFields">*</span></label>
                <input type="text" class="form-input" v-model="form.nu_documento" :disabled="saving" :placeholder="$t('checkinEditDrawer.placeholders.docNumberGeneric')" />
              </div>
              <div class="form-group" v-if="showSupportField">
                <label>{{ $t("checkinEditDrawer.fields.supportNumber") }} <span v-if="requiresSupportNumber">*</span></label>
                <input type="text" class="form-input" v-model="form.document_support" :disabled="saving" :placeholder="requiresSupportNumber ? $t('checkinEditDrawer.placeholders.required') : $t('checkinEditDrawer.placeholders.optional')" />
              </div>
              <div class="form-group form-group--full">
                <label>{{ $t("checkinEditDrawer.fields.nationality") }} *</label>
                <select class="form-input" v-model="form.pais_code" :disabled="saving">
                  <option value="" disabled>{{ $t("checkinEditDrawer.placeholders.selectNationality") }}</option>
                  <option v-for="c in nationalityOptions" :key="c.iso2" :value="c.iso2">{{ c.es }} ({{ c.iso2 }})</option>
                </select>
                <small class="hint" v-if="!requiresDocFields">
                  {{ $t("checkinEditDrawer.hints.docOptional") }}
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- ─── FOOTER ──────────────────────────────────────────────────── -->
        <div class="drawer-footer">
          <button class="wk-btn wk-btn--ghost" @click="close" :disabled="saving" type="button">
            {{ $t("checkinEditDrawer.footer.cancel") }}
          </button>
          <button class="wk-btn wk-btn--primary" @click="save" :disabled="!canSave || saving" type="button">
            <i :class="saving ? 'fa-solid fa-spinner fa-spin' : 'fa-regular fa-check-circle'"></i>
            {{ saving ? $t("checkinEditDrawer.footer.saving") : $t("checkinEditDrawer.footer.save") }}
          </button>
        </div>

      </div>
    </div>
</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=Inter:wght@400;500;600&display=swap');
</style>

<style lang="scss" scoped>
// ── Variables ──────────────────────────────────────────────────────────────
$font-main:  'DM Sans', -apple-system, sans-serif;
$font-small: 'Inter', -apple-system, sans-serif;
$radius:  14px;
$radius-s: 10px;
$blue:   #2563eb;
$border: #e5e7eb;
$text:   #111827;
$muted:  #6b7280;
$bg-soft: #f8fafc;

// ── Overlay / Container ────────────────────────────────────────────────────
.drawer-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 12010; /* por encima del Sidebar del detalle de reserva (baseZIndex 11050) */
  display: flex; justify-content: flex-end;
}
.drawer-container {
  font-family: $font-main;
  width: 520px; max-width: 92vw;
  background: #fff; height: 100vh;
  display: flex; flex-direction: column;
  box-shadow: -4px 0 28px rgba(0,0,0,.14);
}

// ── Header ─────────────────────────────────────────────────────────────────
.drawer-header {
  padding: 18px 20px;
  border-bottom: 1px solid $border;
  display: flex; align-items: flex-start;
  justify-content: space-between; gap: 16px;
}
.drawer-title    { font-family: $font-main; font-size: 15px; font-weight: 800; color: $text; }
.drawer-subtitle { font-family: $font-small; font-size: 12px; color: $muted; font-weight: 600; margin-top: 3px; }
.drawer-meta     { font-family: $font-small; font-size: 11px; color: $muted; font-weight: 500; margin-top: 4px; }
.drawer-close {
  width: 34px; height: 34px; border-radius: $radius-s;
  background: #f3f4f6; border: 1px solid $border; color: $text;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

// ── Body ───────────────────────────────────────────────────────────────────
.drawer-body {
  flex: 1; overflow-y: auto;
  padding: 18px 20px; background: $bg-soft;
}
.drawer-alert {
  background: #fff1f2; border: 1px solid #fecaca; color: #9f1239;
  padding: 12px 14px; border-radius: $radius-s;
  font-family: $font-small; font-size: 13px; font-weight: 600;
  margin-bottom: 14px; display: flex; align-items: center; gap: 10px;
}

// ── Sections ───────────────────────────────────────────────────────────────
.drawer-section {
  margin-bottom: 14px; background: #fff;
  border: 1px solid $border; border-radius: $radius; padding: 14px;
  &--italy { border-color: #c7d2fe; background: #fafbff; }
}
.section-header {
  display: flex; align-items: center; gap: 8px;
  font-family: $font-main; font-size: 13px; font-weight: 800; color: $text;
  margin-bottom: 12px; padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
  i { color: $blue; font-size: 14px; }
  span { font-family: $font-main; }
  &--italy i { color: #4338ca; }
}
.section-flag  { font-size: 16px; line-height: 1; }
.section-badge {
  margin-left: auto;
  font-family: $font-small; font-size: 11px; font-weight: 700;
  background: #eef2ff; color: #3730a3;
  border-radius: 999px; padding: 2px 10px;
}

// ── Form grid ──────────────────────────────────────────────────────────────
.form-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;
}
.form-group {
  display: flex; flex-direction: column; gap: 6px;
  &--full { grid-column: 1 / -1; }
}
.form-group label {
  font-family: $font-small; font-size: 12px; font-weight: 700; color: $muted;
}

// ── Inputs ─────────────────────────────────────────────────────────────────
.form-input {
  font-family: $font-main;
  border: 1px solid #d1d5db; border-radius: $radius-s;
  padding: 10px 12px; font-size: 13px; font-weight: 600;
  color: $text; background: #fff; width: 100%;
  &:focus { outline: none; border-color: $blue; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
  &--search {
    border-bottom-left-radius: 0; border-bottom-right-radius: 0;
    border-bottom-color: #e0e7ff; background: #f5f7ff;
    margin-bottom: 0;
    & + select { border-top-left-radius: 0; border-top-right-radius: 0; border-top-color: #e0e7ff; }
  }
}

// ── Field preview (código Alloggiati) ──────────────────────────────────────
.field-preview {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-top: 6px; padding: 6px 10px;
  background: #f0f4ff; border-radius: 8px;
  border: 1px solid #c7d2fe;
}
.fp-label { font-family: $font-small; font-size: 11px; color: #4338ca; font-weight: 600; }
.fp-code  { font-family: monospace; font-size: 12px; color: #1e3a8a; font-weight: 700; background: #e0e7ff; padding: 1px 6px; border-radius: 4px; }

// ── Minor note ─────────────────────────────────────────────────────────────
.italy-minor-note {
  margin-top: 10px; padding: 10px 12px;
  background: #eff6ff; border: 1px solid #bfdbfe; border-radius: $radius-s;
  font-family: $font-small; font-size: 12px; color: #1d4ed8; font-weight: 600;
  display: flex; align-items: flex-start; gap: 8px;
  i { margin-top: 1px; flex-shrink: 0; }
}

// ── Hints ──────────────────────────────────────────────────────────────────
.hint {
  font-family: $font-small; font-size: 12px; color: $muted;
  display: inline-block; margin-top: 4px;
}
.hint-inline {
  font-family: $font-small; font-size: 11px; color: $muted; font-weight: 500;
}

// ── Footer ─────────────────────────────────────────────────────────────────
.drawer-footer {
  padding: 14px 20px; border-top: 1px solid $border;
  display: flex; gap: 10px; justify-content: flex-end; background: #fff;
}
.wk-btn {
  font-family: $font-main;
  border-radius: $radius-s; padding: 10px 16px;
  font-size: 13px; font-weight: 800;
  border: 1px solid transparent;
  display: inline-flex; gap: 8px; align-items: center; cursor: pointer;
  &:disabled { opacity: .6; cursor: not-allowed; }
}
.wk-btn--ghost   { background: #f3f4f6; border-color: $border; color: $text; }
.wk-btn--primary { background: $blue; border-color: $blue; color: #fff; }

// ── VueTelInput ────────────────────────────────────────────────────────────
.vue-tel-input {
  height: 44px !important; border-radius: 8px !important;
  border: 1px solid #e2e8f0 !important; font-family: $font-main !important;
  &:focus-within { border-color: $blue !important; }
}

// ── Transición drawer ─────────────────────────────────────────────────────
.drawer-enter-active, .drawer-leave-active { transition: opacity .25s ease; }
.drawer-enter-active .drawer-container,
.drawer-leave-active .drawer-container    { transition: transform .25s ease; }
.drawer-enter, .drawer-leave-to           { opacity: 0; }
.drawer-enter .drawer-container,
.drawer-leave-to .drawer-container        { transform: translateX(100%); }

// ── Mobile ────────────────────────────────────────────────────────────────
@media (max-width: 768px) {
  .drawer-container { width: 100vw; max-width: 100vw; }
  .form-grid { grid-template-columns: 1fr; }
}
</style>
